// Banana 2.1 regeneration: .artifacts/cartoon-anime-banana-2026-10-26/records.
// Generation records: .artifacts/image-effects-goal-2026-10-26/records and .artifacts/cartoon-anime-directions-2026-10-26/records.
export interface ImageEffectSample {
	key: string;
	before: string;
	after: string;
	prompt: string;
	aspectRatio: string;
	descriptionKey?: string;
	buzzCut?: {
		targetHairstyle: string;
		hairColor: string;
		otherIdeas: string;
	};
}

export const imageEffectSamples: Record<
	"pixar" | "anime" | "cartoon" | "smile" | "buzz" | "emoji",
	ImageEffectSample[]
> = {
	pixar: [
		{
			key: "socialMedia",
			before: "/images/photo-to-pixar/man-casual-portrait.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/pixar-socialMedia.webp?v=f0243f7a67",
			prompt: "Transform the photo into Pixar-style art, create an expressive profile portrait with rich colors and soft cinematic lighting. Preserve the recognizable face and hairstyle.",
			aspectRatio: "3:4",
		},
		{
			key: "gifts",
			before: "/images/photo-to-pixar/wedding-couple.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/pixar-gifts.webp?v=8c035f6085",
			prompt: "Transform the photo into Pixar-style art, create a warm keepsake portrait of the couple with romantic golden lighting. Preserve both people and their poses.",
			aspectRatio: "3:4",
		},
		{
			key: "content",
			before: "/images/photo-to-pixar/woman-black-portrait.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/pixar-content.webp?v=296c6a4cfa",
			prompt: "Transform the photo into Pixar-style art, create a vivid editorial character portrait with a lavender backdrop. Preserve the recognizable face, hair, and outfit.",
			aspectRatio: "3:4",
		},
		{
			key: "gaming",
			before: "/images/photo-to-pixar/child.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/pixar-gaming.webp?v=2fca064872",
			prompt: "Transform the photo into Pixar-style art, create a playful adventure character portrait with a colorful storybook forest background. Preserve the child's age and identity.",
			aspectRatio: "3:4",
		},
		{
			key: "business",
			before: "/images/image-to-emoji/man-headshot.jpg",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/pixar-business.webp?v=9b10c7276f",
			prompt: "Transform the photo into Pixar-style art, create a polished professional avatar with a clean teal studio background. Preserve the recognizable face and clothing.",
			aspectRatio: "3:4",
		},
		{
			key: "nft",
			before: "/images/image-to-emoji/smiling-woman-headshot.jpg",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/pixar-nft.webp?v=a090848c4a",
			prompt: "Transform the photo into Pixar-style art, create a distinctive collectible character portrait with a peach and violet background, expressive eyes, and detailed hair. Preserve the recognizable face.",
			aspectRatio: "3:4",
		},
	],
	anime: [
		{
			key: "socialMedia",
			before: "/images/photo-to-anime/man-portrait.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/anime-socialMedia.webp?v=4bc2a615cc",
			prompt: "Transform the photo into anime style, create an expressive profile portrait with rich colors and soft cinematic lighting. Preserve the recognizable face and hairstyle.",
			aspectRatio: "3:4",
		},
		{
			key: "gifts",
			before: "/images/photo-to-anime/couple.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/anime-gifts.webp?v=d426348ea9",
			prompt: "Transform the photo into anime style, create a warm keepsake portrait of the couple with romantic golden lighting. Preserve both people and their poses.",
			aspectRatio: "3:4",
		},
		{
			key: "content",
			before: "/images/ai-nano-banana/anime-original.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/anime-content.webp?v=d7bfcd6c0f",
			prompt: "Transform this portrait into a beautifully drawn frame from a contemporary Japanese 2D animated feature. Preserve the same person: light blonde twin braids with red ties, blue eyes, freckles, black cap and black-and-white outfit, recognizable face angle and calm direct gaze. Keep her age and skin tone unchanged. Use elegant fine linework, carefully designed angular hair clumps with crisp ribbon-shaped highlights, expressive detailed irises, restrained stylization of the nose and mouth, and sharply separated two-tone cel shadows. Place her in a softly detailed evening city street with teal shop-window reflections and warm amber sunset rim light; the character remains the clear focal point. Tight chest-up framing with head and cap fully inside the image, cinematic depth, a few wind-swept strands and delicate atmospheric color. A finished hand-drawn animation key visual, not a photograph with an outline filter. No Western caricature proportions, chibi, flat vector mascot, 3D render, plastic skin, oversaturated neon, readable lettering or watermark.",
			aspectRatio: "3:4",
		},
		{
			key: "gaming",
			before: "/images/photo-to-anime/child.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/anime-gaming.webp?v=170a4a8aed",
			prompt: "Transform the photo into anime style, create a playful adventure character portrait with a colorful storybook forest background. Preserve the child's age and identity.",
			aspectRatio: "3:4",
		},
		{
			key: "business",
			before: "/images/image-to-emoji/man-headshot.jpg",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/anime-business.webp?v=5a3bb3b8a2",
			prompt: "Transform the photo into anime style, create a polished professional avatar with a clean teal studio background. Preserve the recognizable face and clothing.",
			aspectRatio: "3:4",
		},
		{
			key: "nft",
			before: "https://assets.imagesart.ai/image-effects/directions-2026-10-26/anime-travel-before.jpg?v=66d78201cb",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/anime-nft.webp?v=1a81f8baff",
			prompt: "Transform this lake photograph into a breathtaking hand-painted background from a Japanese 2D animated travel film. Faithfully retain the recognizable mountain silhouettes, forested shores, lake viewpoint and two wooden rowboats in their original positions. Paint turquoise transparent water with graceful simplified ripples, amber wooden boats, layered deep-green pines and softly lit lavender-blue mountain faces. Use rich opaque gouache-like color shapes, delicately drawn architectural and boat details, luminous cloud edges and atmospheric distance; make it visibly illustrated rather than a photograph with added saturation. Create peaceful late-afternoon sunlight while preserving the original scene layout. No characters, extra boats, buildings, floating objects, fantasy architecture, lettering, borders or watermark. Compose a complete vertical travel illustration with foreground boats and mountain peaks both visible.",
			aspectRatio: "3:4",
		},
	],
	cartoon: [
		{
			key: "socialMedia",
			before: "/images/ai-nano-banana/selfie-transformation-original.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/cartoon-socialMedia.webp?v=8254a1fd82",
			prompt: "Reimagine the person in this photo as a lively original Western 2D cartoon avatar. Preserve her recognizable warm skin tone, dark roots and blonde highlighted hair, sunglasses resting on her head, dark teal jacket, head angle and smile. Deliberately redesign the anatomy as a cartoon: slightly oversized rounded head, small rounded nose, simple expressive almond eyes with dark pupils, bold arched brows, broad asymmetric joyful grin and compact shoulders. Build the hair from a few strong curved graphic shapes. Use confident varied-width ink contours, clean flat color fills and one simple cel-shadow shape per form. Frame head and shoulders with the whole hairstyle and glasses visible against a solid warm coral background. Make the pose and silhouette playful and distinctive, like an original animated sitcom character. Avoid realistic portrait proportions, fine hair strands, intricate crosshatching, glossy giant anime eyes, manga, 3D rendering and photorealistic shading. No lettering or watermark.",
			aspectRatio: "3:4",
		},
		{
			key: "gifts",
			before: "/images/photo-to-cartoon/wedding-couple.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/cartoon-gifts.webp?v=7d3cd29975",
			prompt: "Transform the photo into cartoon style art, use a playful Western 2D comic illustration style with bold thick ink outlines, flat saturated color blocks, simple geometric shapes, and charming exaggerated expressions. Avoid anime or manga aesthetics, realistic shading, and 3D rendering. create a warm keepsake portrait of the couple with romantic golden lighting. Preserve both people and their poses.",
			aspectRatio: "3:4",
		},
		{
			key: "content",
			before: "/images/photo-to-cartoon/woman-black-portrait.webp",
			after: "https://assets.imagesart.ai/image-effects/cartoon-refine-2026-10-26/cartoon-content.webp?v=3b192b8748",
			prompt: "Redesign the woman in this photograph as a beautiful, appealing original 2D animated film character for an editorial creator portrait. Preserve her recognizable face, age, brown wavy hair, black turtleneck, head turn and hand near the temple. Give her a warm subtle smile, relaxed eyebrows and lively eyes with moderate stylization. Use graceful rounded shapes, delicate tapered warm-brown contours rather than thick black outlines, elegant simplified flowing hair shapes, and two or three softly separated cel-shading tones. Keep the nose small and naturally shaped, lips softly defined, anatomy balanced and expression welcoming. An accomplished hand-drawn cartoon character design, visibly illustrated and gently stylized, with clean shapes that read at thumbnail size. Chest-up portrait, full top of the hair in frame, softly glowing peach-and-cream background with a simple warm halo. Tasteful warm color palette with the dark outfit providing contrast. No heavy eyeliner, harsh facial lines, extreme caricature, oversized mouth, photorealism, 3D rendering, anime eyes, text, logos or watermarks.",
			aspectRatio: "3:4",
		},
		{
			key: "gaming",
			before: "/images/photo-to-cartoon/child.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/cartoon-gaming.webp?v=344f6d4bb3",
			prompt: "Transform the photo into cartoon style art, use a playful Western 2D comic illustration style with bold thick ink outlines, flat saturated color blocks, simple geometric shapes, and charming exaggerated expressions. Avoid anime or manga aesthetics, realistic shading, and 3D rendering. create a playful adventure character portrait with a colorful storybook forest background. Preserve the child's age and identity.",
			aspectRatio: "3:4",
		},
		{
			key: "business",
			before: "/images/ai-nano-banana/image-blending-cat-photo.webp",
			after: "https://assets.imagesart.ai/image-effects/banana-2-1-2026-10-26/cartoon-business.webp?v=79a6a8426d",
			prompt: "Turn this exact cat into a charming original 2D cartoon mascot for a pet brand. Keep the tabby forehead M marking, white muzzle and chest, white paws, yellow-green eyes and pink nose recognizable. Design a deliberately exaggerated silhouette: a big round head, small pear-shaped sitting body, chunky paws and one curled tail. Give it a cheeky friendly grin with one slightly raised eyebrow. Use clean tapered ink contours, simple flat shapes, a restrained cream, charcoal and warm caramel palette, and just one hard-edged shadow tone. Center the whole cat with generous breathing room on a warm pale apricot background with a small oval ground shadow. The result should feel like a professionally designed hand-drawn animation character, readable at tiny avatar size. No realistic fur strands, airbrush rendering, anime eyes, manga, 3D rendering, lettering, logo, watermark or sticker border.",
			aspectRatio: "3:4",
		},
		{
			key: "nft",
			before: "/images/image-to-emoji/smiling-woman-headshot.jpg",
			after: "https://assets.imagesart.ai/image-effects/cartoon-refine-2026-10-26/cartoon-nft.webp?v=16532a513f",
			prompt: "Turn the woman in this photo into a joyful, appealing original 2D animated character portrait for a polished digital art print. Preserve her recognizable curly auburn hair, freckles, green eyes, age and friendly expression. Reinterpret the smile as a relaxed natural smile with a small visible upper row of teeth, never a wide-open mouth. Keep eyes softly open, cheeks rounded and facial proportions harmonious. Draw flowing auburn curls as a few elegant broad shapes, with fine tapered cinnamon-colored contour lines, soft cel shading and subtle painted highlights. Use a fresh sage-green blouse and a simple pale mint-and-cream background with warm sunlight and a few abstract rounded leaf shapes. A refined hand-drawn cartoon character with personality, readable silhouette, expressive but gentle features, chest-up composition with complete hair silhouette. Avoid thick black outlines, exaggerated wrinkles, sharp cheek creases, tiny squinting eyes, big teeth, pop-art patterns, stars, stickers, neon colors, comic-book inking, anime eyes, photorealism, 3D rendering, text and watermarks.",
			aspectRatio: "3:4",
		},
	],
	smile: [
		{
			key: "professionalProfiles",
			before: "https://assets.imagesart.ai/templates/ai-smile-generator/carousel/before-01.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/smile-professionalProfiles.webp?v=33ce41fa87",
			prompt: "Add a warm, natural smile to the person in this photo. Preserve their identity, facial proportions, age, hair, clothing, pose, and background. Keep realistic teeth, natural smile lines, and a believable expression.",
			aspectRatio: "3:4",
		},
		{
			key: "socialMedia",
			before: "https://assets.imagesart.ai/templates/ai-smile-generator/carousel/before-02.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/smile-socialMedia.webp?v=c012a4e785",
			prompt: "Add a warm, natural smile to the person in this photo. Preserve their identity, facial proportions, age, hair, clothing, pose, and background. Keep realistic teeth, natural smile lines, and a believable expression.",
			aspectRatio: "3:4",
		},
		{
			key: "familyPhotos",
			before: "/images/age-filter/woman-portrait-2.png",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/smile-familyPhotos.webp?v=cdceb54d15",
			prompt: "Add a warm, natural smile to the person in this photo. Preserve their identity, facial proportions, age, hair, clothing, pose, and background. Keep realistic teeth, natural smile lines, and a believable expression.",
			aspectRatio: "3:4",
		},
		{
			key: "weddingPhotography",
			before: "/images/photo-to-pixar/wedding-couple.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/smile-weddingPhotography.webp?v=a36123b2fe",
			prompt: "Add warm, natural smiles to both people in this wedding photo. Preserve both faces, identities, ages, clothing, pose, and background. Keep realistic teeth and natural smile lines.",
			aspectRatio: "3:4",
		},
		{
			key: "datingProfiles",
			before: "/images/age-filter/man-portrait.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/smile-datingProfiles.webp?v=c1358ea381",
			prompt: "Add a warm, natural smile to the person in this photo. Preserve their identity, facial proportions, age, hair, clothing, pose, and background. Keep realistic teeth, natural smile lines, and a believable expression.",
			aspectRatio: "3:4",
		},
		{
			key: "funMemes",
			before: "/images/age-filter/woman-portrait-3.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/smile-funMemes.webp?v=b62526ddea",
			prompt: "Add a bright, joyful smile to the person in this photo. Preserve their identity, facial proportions, age, hair, clothing, pose, and background. Keep realistic teeth, natural smile lines, and a believable expression.",
			aspectRatio: "3:4",
		},
	],
	buzz: [
		{
			key: "preview",
			before: "/images/comparisons/man1-hairstyle.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/buzz-preview.webp?v=c24466a312",
			prompt: "Transform the person in this image to have a buzz cut with dark brown hair. Keep the existing lighting and background. Keep the person's facial features and expression unchanged. Make the result look natural and realistic.",
			aspectRatio: "3:4",
			buzzCut: {
				targetHairstyle: "buzz cut",
				hairColor: "dark brown",
				otherIdeas: "Keep the existing lighting and background.",
			},
		},
		{
			key: "boldLook",
			before: "/images/comparisons/woman-hairstyle.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/buzz-boldLook.webp?v=b978cd9e00",
			prompt: "Transform the person in this image to have a buzz cut with black hair. Keep the hairline natural and the face clearly recognizable. Keep the person's facial features and expression unchanged. Make the result look natural and realistic.",
			aspectRatio: "3:4",
			buzzCut: {
				targetHairstyle: "buzz cut",
				hairColor: "black",
				otherIdeas:
					"Keep the hairline natural and the face clearly recognizable.",
			},
		},
		{
			key: "barber",
			before: "/images/comparisons/man2-hairstyle.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/buzz-barber.webp?v=f9d1627528",
			prompt: "Transform the person in this image to have a buzz cut with salt-and-pepper hair. Keep the original hairline and natural hair texture. Keep the person's facial features and expression unchanged. Make the result look natural and realistic.",
			aspectRatio: "3:4",
			buzzCut: {
				targetHairstyle: "buzz cut",
				hairColor: "salt-and-pepper",
				otherIdeas:
					"Keep the original hairline and natural hair texture.",
			},
		},
	],
	emoji: [
		{
			key: "pet",
			before: "/images/ai-nano-banana/image-blending-cat-photo.webp",
			after: "https://assets.imagesart.ai/image-effects/2026-10-26/emoji-pet.webp?v=8410b1bc6b",
			prompt: "Turn the cat in this photo into one charming custom 3D emoji sticker. Preserve its recognizable fur colors, markings, eye color, and ear shape. Use rounded expressive features and soft dimensional shading. Center the full head and upper body in a square frame, with a crisp thick white die-cut outline around the silhouette. Use a softly blurred pastel sky-blue to mint-green gradient background. No text, extra animals, collage, checkerboard, or watermark.",
			aspectRatio: "1:1",
		},
	],
};
